import { axiosBaseQuery, request } from 'react-common'
import { createApi } from '@reduxjs/toolkit/query/react'
import { ActivityRecord } from '@/api/models/activityRecord'
import {
  ActivityPathId,
  ActivityWithAggregate,
  ObjectiveWorkflowAggregateId,
  ObjectiveWorkflowId,
} from '@/api/models/aggregate'

const BASE_URL = 'users/me/objective-workflows'

export const objectiveWorkflowApi = createApi({
  reducerPath: 'objectiveWorkflowApi',
  baseQuery: axiosBaseQuery(request),
  endpoints: (build) => ({
    fetchActivityWithAggregateByObjectiveWorkflowAggregate: build.query<
      ActivityWithAggregate,
      { objectiveWorkflowId: ObjectiveWorkflowId; activityPathId?: ActivityPathId; standalone?: boolean }
    >({
      query: ({ objectiveWorkflowId, activityPathId }) => {
        let url = `${BASE_URL}/${objectiveWorkflowId}/activity`

        if (activityPathId) url += `/${encodeURIComponent(activityPathId)}`

        return {
          url,
          method: 'get',
        }
      },
    }),
    fetchActivityWorkflowAggregateFullTree: build.query<
      ActivityWithAggregate,
      { objectiveWorkflowId: ObjectiveWorkflowId }
    >({
      query: ({ objectiveWorkflowId }) => ({
        url: `${BASE_URL}/${objectiveWorkflowId}/activity-with-aggregates`,
        method: 'get',
      }),
    }),
    fetchActivityWithAggregateByObjectiveWorkflowChildren: build.query<
      ActivityWithAggregate[],
      { objectiveWorkflowId: ObjectiveWorkflowId; activityPathId?: ActivityPathId }
    >({
      query: ({ objectiveWorkflowId, activityPathId }) => {
        const url = activityPathId
          ? `${BASE_URL}/${objectiveWorkflowId}/activity/${encodeURIComponent(activityPathId)}/children`
          : `${BASE_URL}/${objectiveWorkflowId}/activity/children`

        return {
          url,
          method: 'get',
        }
      },
    }),
    fetchActivityRecords: build.query<
      ActivityRecord[],
      { query: Record<string, any>; objectiveWorkflowAggregateId: ObjectiveWorkflowAggregateId }
    >({
      query: ({ query, objectiveWorkflowAggregateId }) => ({
        url: `users/me/objective-workflow-aggregates/${objectiveWorkflowAggregateId}/activity-records/query`,
        method: 'get',
        params: { query },
      }),
      transformResponse: (resp: { data: ActivityRecord[] }) => resp.data,
    }),
  }),
})

export const {
  useFetchActivityWithAggregateByObjectiveWorkflowAggregateQuery,
  /* INFO: activity info should be got from useLazyFetchActivityWithAggregateByObjectiveWorkflowAggregateQuery */
  useLazyFetchActivityWithAggregateByObjectiveWorkflowAggregateQuery,
  useLazyFetchActivityWorkflowAggregateFullTreeQuery,
  useLazyFetchActivityWithAggregateByObjectiveWorkflowChildrenQuery,
  useLazyFetchActivityRecordsQuery,
} = objectiveWorkflowApi
